import {
  BulbOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  ActivatingEvent,
  baseEmotionsEn,
  baseEmotionsRu,
  baseFeelingsEn,
  baseFeelingsRu,
  dbCols,
  translationEn,
  translationRu,
} from '@cc/schema';
import { timeout } from '@cc/utils';
import { Create } from '@refinedev/antd';

import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Slider,
  Spin,
  Tooltip,
  Typography,
} from 'antd';

import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';
import { useWatch } from 'antd/lib/form/Form';
import { isEqual } from 'lodash';
import qs from 'qs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'rooks';
import { useClientOptions } from '../../hooks/client-options';
import {
  BehaviorExperiment,
  BehaviorExperimentSuggestions,
} from '../../schema/be-graphql-generated';
import * as Sentry from '@sentry/react';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import useStyles from './styles.style';
import { Link, useLocation } from 'react-router-dom';

import { SelectClient } from '../../components/SelectClient';
import {
  graphqlErrorInfo,
  hasGraphQLErrorCode,
} from '../../utils/graphqlErrors';
import {
  BEHAVIOR_EXPERIMENT_DOMAIN,
  COEXISTENCE_COPY_KEYS,
  completionInputFromLegacyOutcome,
  designInputFromForm,
  suggestionsToVariant,
  type DesignFormValues,
} from './esHelpers';
import {
  BEHAVIOR_EXPERIMENT_ES_FIELDS,
  BEHAVIOR_EXPERIMENT_SETTINGS_FIELDS,
} from './fields';
import { useClientEra } from './useClientEra';

/** The AI answer's selection — the GptBehaviorExperiment read model IS the suggestions payload. */
const SUGGESTION_FIELDS = [
  'purpose',
  'design',
  'hypothesisType',
  'type',
  'activeSubtype',
  'observationalSubtype',
  'hypothesisA',
  'hypothesisAStrength',
  'hypothesisAConfirmationCriteria',
  'hypothesisB',
  'hypothesisBStrength',
  'hypothesisBConfirmationCriteria',
  { settings: BEHAVIOR_EXPERIMENT_SETTINGS_FIELDS },
  'safetyBehaviors',
  'helpfulCopingStrategies',
  {
    prediction: [
      { emotions: ['id', 'strength'] },
      'feelings',
      'behaviors',
      'otherPredictions',
    ],
  },
  'followUpExperimentsIdeas',
];

/** What a design / completion payload returns to the editor — enough to update the page state. */
const PAYLOAD_ROW_FIELDS = [
  '_id',
  'experimentId',
  'status',
  'activatedAt',
  'completedAt',
];

type PayloadRow = {
  _id: string;
  experimentId?: string | null;
  status?: string | null;
};

const { Title } = Typography;

const { OptGroup, Option } = Select;

export const BehaviorExperimentCreate = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const { styles } = useStyles();

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const { clientsLoading } = useClientOptions();

  const [generatedVariants, setGeneratedVariants] = useState<any[]>([]);
  const [generatedVariantValue, setGeneratedVariantValue] = useState<number>();

  const { setWarnWhen } = useWarnAboutChange();
  const contentRef = useRef('');

  const getContent = () => {
    return contentRef.current;
  };

  useEffect(() => {
    return () => {
      contentRef.current = '';
    };
  }, []);

  const { clientId: locationClientId, behaviorExperimentId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const { innerWidth } = useWindowSize();

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState?.context?.filterClientId,
  );

  const baseEmotions = useMemo(() => {
    const baseEmotions = currentLocale?.startsWith('ru')
      ? baseEmotionsRu
      : baseEmotionsEn;

    return baseEmotions.map((e) => ({
      ...e,
      children: e.children.map((ch) => ({ ...ch, strength: 50 })),
    }));
  }, [currentLocale]);

  const flattenedEmotions = useMemo(() => {
    return baseEmotions.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseEmotionsEn)[0]['children'],
    );
  }, [baseEmotions]);

  const baseFeelings = currentLocale?.startsWith('ru')
    ? baseFeelingsRu
    : baseFeelingsEn;

  const initialValues = useMemo(() => {
    return {
      purpose: undefined as number | undefined,
      design: undefined as number | undefined,
      hypothesisType: undefined as number | undefined,
      hypothesisA: '' as string | undefined | null,
      hypothesisAStrength: 50 as number | undefined | null,
      hypothesisAConfirmationCriteria: '' as string | undefined | null,
      hypothesisB: '' as string | undefined | null,
      hypothesisBStrength: 50 as number | undefined | null,
      hypothesisBConfirmationCriteria: '' as string | undefined | null,
      type: undefined as number | undefined,
      activeSubtype: undefined as number | undefined | null,
      observationalSubtype: undefined as number | undefined | null,
      settings: {
        experiment: '' as string | undefined | null,
        time: '' as string | undefined | null,
        place: '' as string | undefined | null,
        people: '' as string | undefined | null,
        resources: '' as string | undefined | null,
        survey: '' as string | undefined | null,
      },
      prediction: {
        emotions: [] as ActivatingEvent['emotions'] | null[],
        feelings: [] as ActivatingEvent['feelings'] | null[],
        behaviors: '' as string | undefined | null,
        otherPredictions: '' as string | undefined | null,
      },
      safetyBehaviors: '' as string | undefined | null,
      helpfulCopingStrategies: '' as string | undefined | null,
      outcome: {
        behaviors: '' as string | undefined | null,
        emotions: [] as ActivatingEvent['emotions'] | null[],
        feelings: [] as ActivatingEvent['feelings'] | null[],
        patientReflections: '' as string | undefined | null,
        therapistReflections: '' as string | undefined | null,
        newLearnings: '' as string | undefined | null,
        meaningForOriginalBelief: '' as string | undefined | null,
        oldThoughtStrength: 50 as number | undefined | null,
        newThought: '' as string | undefined | null,
        newThoughtStrength: 50 as number | undefined | null,
      },
      followUpExperimentsIdeas: '' as string | undefined | null,
    };
  }, [therapistActivityState?.context?.filterClientId || locationClientId]);

  const [form] = Form.useForm<typeof initialValues>();

  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const type = useWatch('type', form);
  const hypothesisType = useWatch('hypothesisType', form);
  const activeSubtype = useWatch('activeSubtype', form);
  const observationalSubtype = useWatch('observationalSubtype', form);
  const hypothesisA = useWatch('hypothesisA', form);
  const hypothesisB = useWatch('hypothesisB', form);

  const outcome = useWatch('outcome', form);

  const [expreimentId, setExpreimentId] = useState(
    behaviorExperimentId as string | undefined,
  );

  // ── E02 · the CLIENT's era decides the command set (plan 07 C3) ────────────────────────────────
  // An `es` client in a READY `behaviorExperiment` domain saves through ONE designBehaviorExperiment
  // (the five shared design events — founder G1 2026-08-29, portal-per-step resolved_v2 (c)); every
  // other client keeps the shipped upsert. The era is asked fresh on every mount and re-asked when
  // the server refuses a write with a coexistence code (HANDOFF T112).
  const { isEsClient, refetchEra } = useClientEra(
    clientId,
    BEHAVIOR_EXPERIMENT_DOMAIN,
  );
  // the row the last ES command returned (read-your-writes) — the page state between loads
  const [esRow, setEsRow] = useState<PayloadRow | null>(null);
  const { mutateAsync: esMutate, isLoading: esLoading } =
    useCustomMutation<any>();

  useActivityTracking(
    expreimentId ? expreimentId : `${dbCols.behaviorExperiments}/create`,
  );

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const removeVariantValues = async (
    formValues: any,
    values: any,
    prefix = [] as string[],
  ) => {
    const keys = Object.keys(values);

    for (const key of keys) {
      // @ts-ignore
      if (values[key] !== undefined && isEqual(values[key], formValues[key])) {
        // @ts-ignore
        if (values[key] instanceof Array) {
          // @ts-ignore
        } else if (typeof values[key] === 'object') {
          await removeVariantValues(formValues[key], values[key], [
            ...prefix,
            key,
          ]);
          // @ts-ignore
        } else if (typeof values[key] === 'string') {
          // @ts-ignore

          form.setFieldValue([...prefix, key], '');
        }
      }
    }
    return;
  };

  const mimicUserTyping = async (
    formValues: any,
    values: any,
    prefix = [] as string[],
  ) => {
    const keys = Object.keys(values);

    for (const key of keys) {
      // @ts-ignore
      if (values[key] !== undefined && !isEqual(values[key], formValues[key])) {
        // @ts-ignore
        if (values[key] instanceof Array) {
          // @ts-ignore
        } else if (typeof values[key] === 'object') {
          await mimicUserTyping(formValues[key], values[key], [...prefix, key]);
          // @ts-ignore
        } else if (typeof values[key] === 'string') {
          // @ts-ignore
          for (let i = 0; i < values[key].length + 1; i++) {
            // @ts-ignore
            form.setFieldValue([...prefix, key], values[key].substring(0, i));
            await timeout(7);
          }
        }
      }
    }
  };

  const maybeRemoveVariantValues = async () => {
    if (generatedVariantValue !== undefined) {
      const {
        purpose,
        design,
        hypothesisType,
        hypothesisAStrength,
        hypothesisBStrength,
        hypothesisA,
        hypothesisB,
        type,
        activeSubtype,
        observationalSubtype,
        outcome,
        ...rest
      } = generatedVariants[generatedVariantValue];

      const { prediction, ...freshFormValues } = form.getFieldsValue();

      const { emotions, feelings, ...restPrediction } = prediction;

      let updatedValues = {} as any;

      if (
        freshFormValues.hypothesisA &&
        freshFormValues.hypothesisB &&
        hypothesisB
      ) {
        updatedValues.hypothesisB = hypothesisB;
      }
      await removeVariantValues(
        { ...freshFormValues, prediction: { ...restPrediction } },
        { ...updatedValues, ...rest },
      );
      form.setFieldValue('prediction.emotions', {
        ...emotions,
      });
      form.setFieldValue('prediction.feelings', {
        ...feelings,
      });
      return;
    }
  };
  const resetValuesFromVariant = async (variant: typeof initialValues) => {
    await maybeRemoveVariantValues();
    const {
      purpose,
      design,
      hypothesisType,
      hypothesisAStrength,
      hypothesisBStrength,
      hypothesisA,
      hypothesisB,
      type,
      activeSubtype,
      prediction,
      observationalSubtype,
      ...rest
    } = variant;

    const { emotions, feelings, ...restPrediction } = prediction;

    const freshFormValues = form.getFieldsValue();

    let updatedValues = {} as any;

    if (!freshFormValues.hypothesisA && hypothesisA) {
      updatedValues.hypothesisA = hypothesisA;
    }
    if (!freshFormValues.hypothesisB && hypothesisB) {
      updatedValues.hypothesisB = hypothesisB;
    }

    mimicUserTyping(freshFormValues, {
      ...updatedValues,
      ...rest,
      prediction: {
        ...restPrediction,
      },
    });
    setHasGenerated(true);
  };

  const {
    data: gptExperimentData,
    isLoading: gptExperimentLoading,
    refetch: refetchGptExperiment,
    error: gptExperimentError,
  } = useCustom<any>({
    url: '',
    method: 'get',
    meta: {
      operation: 'generateGptBehaviorExperiment',
      fields: ['response'],
      variables: {
        content: {
          name: 'content',
          type: 'String!',
          value: getContent(),
        },
        language: {
          name: 'language',
          type: 'String!',
          value: currentLocale,
        },
      },
    },
  });

  const [generateRetriedOnce, setGenerateRetriedOnce] = useState(false);

  const onGenerateHypothesis = async () => {
    await maybeRemoveVariantValues();
    const values = form.getFieldsValue();

    const translation = currentLocale?.startsWith('ru')
      ? translationRu
      : translationEn;
    let requestData: any = {
      purpose:
        translation.shared.behaviorExperiment.purposes[values.purpose || 0],
      design: translation.shared.behaviorExperiment.designs[values.design || 0],
      hypothesisType:
        translation.shared.behaviorExperiment.hypothesisTypeVariants[
          values.hypothesisType || 0
        ],

      hypothesisA: values.hypothesisA,
      hypothesisAConfirmationCriteria: values.hypothesisAConfirmationCriteria,
      hypothesisB: values.hypothesisB,
      hypothesisBConfirmationCriteria: values.hypothesisBConfirmationCriteria,
      type: translation.shared.behaviorExperiment.typeVariants[
        values.type || 0
      ],

      settings: {
        experiment: values.settings.experiment,
        time: values.settings.time,
        place: values.settings.place,
        people: values.settings.people,
        resources: values.settings.resources,
        survey: values.settings.survey,
      },
      prediction: {
        behaviors: values.prediction.behaviors,
        otherPredictions: values.prediction.otherPredictions,
      },
      safetyBehaviors: values.safetyBehaviors,
      helpfulCopingStrategies: values.helpfulCopingStrategies,
      followUpExperimentsIdeas: values.followUpExperimentsIdeas,
    };

    requestData =
      values.type === 0
        ? {
            ...requestData,
            activeSubtype:
              translation.shared.behaviorExperiment.activeSubtypes[
                values.activeSubtype || 0
              ],
          }
        : {
            ...requestData,

            observationalSubtype:
              translation.shared.behaviorExperiment.observationalSubtypes[
                values.observationalSubtype || 0
              ],
          };

    if (isEsClient) {
      // the E-era AI lane (RequestBehaviorExperimentSuggestions → the model → GptBehaviorExperiment)
      await requestSuggestionsEs(requestData);
      return;
    }

    try {
      setIsGenerating(true);

      contentRef.current = JSON.stringify(requestData);

      await timeout(100);
      const res = await refetchGptExperiment();
      const response = res?.data?.data?.response || JSON.stringify({});
      let parsedResonse = {} as any;
      try {
        parsedResonse = JSON.parse(response);
      } catch (error) {
        Sentry.captureException(error);
      }
      if (!generateRetriedOnce && isEqual(parsedResonse, {})) {
        setGenerateRetriedOnce(true);
        await timeout(50);
        await onGenerateHypothesis();
        return;
      }
      setGenerateRetriedOnce(false);
      const lastMessage = parsedResonse?.choices?.[0]?.message;
      if (lastMessage) {
        const isAssistant = lastMessage?.role === 'assistant';
        if (isAssistant && lastMessage?.content) {
          let lastGeneratedExperiment = {} as unknown as typeof initialValues;
          try {
            lastGeneratedExperiment = JSON.parse(lastMessage.content);
          } catch (error) {
            Sentry.captureException(error);
          }
          const newGenratedVariants = [
            ...generatedVariants,
            lastGeneratedExperiment,
          ];

          setGeneratedVariants(newGenratedVariants);
          await resetValuesFromVariant(lastGeneratedExperiment);
          setGeneratedVariantValue(newGenratedVariants.length - 1);
        }
      }
    } catch (error) {
      message.error(t('shared.behaviorExperiment.generationError'));
      Sentry.captureException(error);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * E02 section (b): `requestBehaviorExperimentSuggestions(content, language)` — the request is
   * recorded, the model asked, the answer recorded and returned in one call (handoff §3). The
   * suggestions populate the form through the shipped variant machinery (typed in, switchable),
   * exactly as the legacy model's answer did; `prediction.emotions/feelings` stay the therapist's
   * pick. A coded failure (GENERATION_FAILED / CONTENT_REQUIRED / USER_NOT_AUTHORIZED) shows the
   * server's actionable copy; anything else the generic generation error.
   */
  const requestSuggestionsEs = async (requestData: unknown) => {
    try {
      setIsGenerating(true);
      const res = await esMutate({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'requestBehaviorExperimentSuggestions',
          fields: ['requestId', { suggestions: SUGGESTION_FIELDS }],
          variables: {
            input: {
              name: 'input',
              type: 'RequestBehaviorExperimentSuggestionsInput!',
              value: {
                content: JSON.stringify(requestData),
                language: currentLocale,
              },
            },
          },
        },
        errorNotification: false,
      });
      const suggestions = (
        res?.data as {
          suggestions?: BehaviorExperimentSuggestions | null;
        } | null
      )?.suggestions;
      if (!suggestions) {
        throw new Error('behavior-experiment-suggestions-empty');
      }
      const variant = suggestionsToVariant(
        suggestions,
      ) as unknown as typeof initialValues;
      const next = [...generatedVariants, variant];
      setGeneratedVariants(next);
      await resetValuesFromVariant(variant);
      setGeneratedVariantValue(next.length - 1);
    } catch (error) {
      const { code, message: serverMessage } = graphqlErrorInfo(error);
      message.error(
        serverMessage || t('shared.behaviorExperiment.generationError'),
      );
      if (code !== 'GENERATION_FAILED' && code !== 'CONTENT_REQUIRED') {
        Sentry.captureException(error);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const memoizedPurposeOptions = useMemo(() => {
    return t('shared.behaviorExperiment.purposes', {
      returnObjects: true,
      // @ts-ignore
    }).map((purpose: any, index: number) => ({
      label: purpose,
      value: index,
    }));
  }, [i18n.language]);

  const memoizedDesignsOptions = useMemo(() => {
    return t('shared.behaviorExperiment.designs', {
      returnObjects: true,
      // @ts-ignore
    }).map((design: any, index: number) => ({
      label: design,
      value: index,
    }));
  }, [i18n.language]);

  const memoizedHypotethisCountOptions = useMemo(() => {
    return t('shared.behaviorExperiment.hypothesisTypeVariants', {
      returnObjects: true,
      // @ts-ignore
    }).map((design: any, index: number) => ({
      label: design,
      value: index,
    }));
  }, [i18n.language]);

  const memoizedTypeVariantsOptions = useMemo(() => {
    return t('shared.behaviorExperiment.typeVariants', {
      returnObjects: true,
      // @ts-ignore
    }).map((design: any, index: number) => ({
      label: design,
      value: index,
    }));
  }, [i18n.language]);

  const memoizedActiveSubtypesOptions = useMemo(() => {
    return t('shared.behaviorExperiment.activeSubtypes', {
      returnObjects: true,
      // @ts-ignore
    }).map((design: any, index: number) => ({
      label: design,
      value: index,
    }));
  }, [i18n.language]);

  const memoizedObservationalSubtypesOptions = useMemo(() => {
    return t('shared.behaviorExperiment.observationalSubtypes', {
      returnObjects: true,
      // @ts-ignore
    }).map((design: any, index: number) => ({
      label: design,
      value: index,
    }));
  }, [i18n.language]);

  const textAlign = (innerWidth || 1000) < 995 ? 'center' : 'left';

  const {
    data: behaviorExperimentData,
    isLoading: behaviorExperimentLoading,
    refetch: refetchBehaviorExperiment,
    error: behaviorExperimentError,
  } = useCustom<BehaviorExperiment>({
    url: '',
    method: 'get',
    meta: {
      operation: 'behaviorExperiment',
      // the legacy row + the E02 additive read fields (experimentId / status / observations …)
      fields: BEHAVIOR_EXPERIMENT_ES_FIELDS,

      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: expreimentId,
        },
      },
    },
  });

  useEffect(() => {
    if (behaviorExperimentData?.data) {
      const {
        __typename,
        _id,
        _key,
        _rev,
        createdAt,
        updatedAt,
        // the E02 read fields are page state, never form values
        therapistId,
        experimentId,
        status,
        activatedAt,
        completedAt,
        observations,
        ...rest
      } = behaviorExperimentData?.data;

      form?.setFieldsValue({
        ...rest,
      });
      setEsRow(null); // the server's row is the new baseline
      setClientId(behaviorExperimentData?.data.userId);

      if (behaviorExperimentData?.data.userId) {
        therapistActivitySend({
          type: 'LOADED',
          resource: behaviorExperimentData?.data?._id,
          clientId: behaviorExperimentData?.data.userId,
        });
      }
    }
  }, [behaviorExperimentData]);

  const {
    mutateAsync: upsertBehaviorExperiment,
    data: upsertBehaviorExperimentData,
    isLoading: upsertBehaviorExperimentLoading,
    error: upsertBehaviorExperimentError,
  } = useCustomMutation<{ behaviorExperiment: BehaviorExperiment }>();

  // ── E02 · which path this save takes ──────────────────────────────────────────────────────────
  // THE ROW DECIDES THE ERA FOR EDITS (HANDOFF T95a / handoff §4): a row WITHOUT `experimentId` for
  // an `es` client is unconverted — the ES commands would fold to NOT_FOUND and the legacy upsert is
  // refused, so the editor blocks the save with an actionable notice until the converter heals it.
  const loadedRow = behaviorExperimentData?.data;
  const esExperimentId = esRow?.experimentId ?? loadedRow?.experimentId ?? null;
  const isEditMode = Boolean(expreimentId);
  const rowLoaded = Boolean(loadedRow?._id);
  const isEsRow = Boolean(esExperimentId);
  const unconvertedEsRow = isEsClient && isEditMode && rowLoaded && !isEsRow;
  const useEsPath = isEsClient && (!isEditMode || isEsRow);

  /**
   * A refused command: the server's (localised) copy for a CODED reject, the generic copy
   * otherwise — except the two COEXISTENCE codes, whose server copy addresses the ACCOUNT OWNER
   * ("Your account was just updated"). The reader here is the therapist (founder QA 2026-09-02).
   */
  const reportEsFailure = (error: unknown) => {
    const { code, message: serverMessage } = graphqlErrorInfo(error);
    const therapistCopyKey = code ? COEXISTENCE_COPY_KEYS[code] : undefined;
    if (therapistCopyKey) {
      message.error(t(therapistCopyKey));
      return;
    }
    if (code && serverMessage) {
      message.error(serverMessage);
      return;
    }
    message.error(t('shared.behaviorExperiment.saveError'));
    Sentry.captureException(error);
  };

  /**
   * E02 section (c): ONE `designBehaviorExperiment`. NEW = setup + the three sections + activate
   * (the portal design is complete on save, as the legacy form was); EDIT = the three sections
   * (sent even when blank — only the changed ones are recorded server-side) + activate, setup
   * untouched. The payload's row updates the page state.
   *
   * @param identity the row's ES identity — the loaded row's by default; the ES_RESEND_AS_ES
   *   fallback passes the identity it just re-read, because the row was learnt before the flip
   *   converted it (T112)
   * @param opts.reissueOutcome T112 mirror: the legacy outcome section's REFLECTION text becomes a
   *   `completeExperiment` right after the design (the run-like fields are observations in the
   *   E-era — the therapist records them from the experiment view)
   */
  const saveDesignEs = async (
    values: DesignFormValues,
    identity: string | null,
    opts: { reissueOutcome?: boolean } = {},
  ) => {
    if (!clientId) return;
    const res = await esMutate({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'designBehaviorExperiment',
        fields: ['experimentId', { behaviorExperiment: PAYLOAD_ROW_FIELDS }],
        variables: {
          input: {
            name: 'input',
            type: 'DesignBehaviorExperimentInput!',
            value: designInputFromForm(values, {
              subject: clientId,
              experimentId: identity,
              activate: true,
            }),
          },
        },
      },
      errorNotification: false,
    });
    const payload = res?.data as
      | { experimentId: string; behaviorExperiment?: PayloadRow | null }
      | null
      | undefined;
    if (!payload?.behaviorExperiment?._id) {
      // a silent null = no permission edge (the E07 gate, as the legacy path answers)
      throw new Error('behavior-experiment-design-missing-row');
    }
    let row: PayloadRow = payload.behaviorExperiment;
    if (opts.reissueOutcome) {
      const completion = completionInputFromLegacyOutcome(values, {
        subject: clientId,
        experimentId: payload.experimentId,
      });
      if (completion) {
        const done = await esMutate({
          url: '',
          method: 'post',
          values: {},
          meta: {
            operation: 'completeExperiment',
            fields: [
              'experimentId',
              { behaviorExperiment: PAYLOAD_ROW_FIELDS },
            ],
            variables: {
              input: {
                name: 'input',
                type: 'CompleteExperimentInput!',
                value: completion,
              },
            },
          },
          errorNotification: false,
        });
        const completed = (
          done?.data as { behaviorExperiment?: PayloadRow | null } | null
        )?.behaviorExperiment;
        if (completed) row = completed;
      }
    }
    setEsRow({
      _id: row._id,
      experimentId: row.experimentId ?? payload.experimentId,
      status: row.status,
    });
    if (!expreimentId) {
      setExpreimentId(row._id);
      therapistActivitySend({ type: 'CREATED', resource: row._id });
    } else {
      therapistActivitySend({ type: 'UPDATED', resource: row._id });
    }
    message.success(t('shared.behaviorExperiment.saveSuccess'));
    setWarnWhen(false);
  };

  const saveData = async (values?: typeof initialValues) => {
    const data = values || form?.getFieldsValue();
    const isReady =
      data.purpose !== undefined &&
      data.design !== undefined &&
      data.hypothesisType !== undefined &&
      data.type !== undefined &&
      (data.type === 0
        ? data.activeSubtype !== undefined
        : data.observationalSubtype !== undefined);

    if (!clientId || !isReady) {
      return;
    }

    if (useEsPath) {
      try {
        await saveDesignEs(data, esExperimentId);
        return;
      } catch (error) {
        // rule 5 (guard-by-server): ES_MODE_MISMATCH means the server routes this client to legacy
        // — fall through to the shipped upsert; anything else is reported by its CODE
        if (!hasGraphQLErrorCode(error, 'ES_MODE_MISMATCH')) {
          reportEsFailure(error);
          return;
        }
        await refetchEra();
      }
    }

    if (unconvertedEsRow) {
      message.warning(t('pages.behaviorExperiment.unconvertedRow'));
      return;
    }

    try {
      await upsertBehaviorExperiment({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'upsertBehaviorExperiment',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'BehaviorExperimentInput',
              value: {
                data,
                where: {
                  clientId,
                  _id: expreimentId,
                },
              },
            },
          },
        },
        // the outcome is reported below — never the raw GraphQL error toast
        errorNotification: false,
      });
      message.success(t('shared.behaviorExperiment.saveSuccess'));
      setWarnWhen(false);
    } catch (error) {
      // rule 5, the OTHER direction (HANDOFF T112): ES_RESEND_AS_ES means this client is already on
      // the ES path for the domain and the era this page loaded with is stale (a flip landed
      // mid-session). Refresh the era, re-read the row's identity (the flip converted it), and
      // re-issue the write as the design command (+ the completion when the outcome carries text).
      if (!hasGraphQLErrorCode(error, 'ES_RESEND_AS_ES')) {
        message.error(t('shared.behaviorExperiment.saveError'));
        Sentry.captureException(error);
        return;
      }
      try {
        let identity: string | null = null;
        if (expreimentId) {
          const fresh = await refetchBehaviorExperiment();
          identity = fresh.data?.data?.experimentId ?? null;
          if (!identity) {
            // flipped, but this row has not been converted yet — the converter heals it (T95a)
            message.warning(t('pages.behaviorExperiment.unconvertedRow'));
            return;
          }
        }
        await refetchEra();
        await saveDesignEs(data, identity, { reissueOutcome: true });
        // re-reading the row above refilled the form from the SERVER copy, which is the version
        // before this edit (the legacy attempt was refused). The design command has since recorded
        // the edit, so put the therapist's own values back on screen — otherwise the save succeeds
        // and the page silently shows the old text.
        form?.setFieldsValue(data);
      } catch (retryError) {
        reportEsFailure(retryError);
      }
    }
  };

  useEffect(() => {
    // @ts-ignore
    if (upsertBehaviorExperimentData?.data?._id) {
      // @ts-ignore

      if (!expreimentId) {
        // @ts-ignore
        setExpreimentId(upsertBehaviorExperimentData?.data?._id);
        therapistActivitySend({
          type: 'CREATED',
          // @ts-ignore
          resource: upsertBehaviorExperimentData?.data?._id,
        });
      } else {
        therapistActivitySend({
          type: 'UPDATED',
          resource: expreimentId,
        });
      }
    }
  }, [upsertBehaviorExperimentData]);

  const generateArea = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 20,
        alignSelf: 'center',
      }}
    >
      <Button
        type="primary"
        onClick={onGenerateHypothesis}
        icon={<BulbOutlined />}
        data-testid="cc-behavior-experiment-generate"
        disabled={
          isGenerating ||
          !(hypothesisA || hypothesisB) ||
          generatedVariants.length >= 3
        }
      >
        {t(
          generatedVariants.length === 0
            ? 'shared.behaviorExperiment.generateGptSuggestions'
            : 'shared.behaviorExperiment.generateGptSuggestionsMore',
        )}
      </Button>

      {isGenerating && (
        <Spin
          style={{
            marginTop: 20,
          }}
          size="large"
          tip={t('shared.behaviorExperiment.generating')}
        />
      )}

      {generatedVariants.length > 0 && (
        <Radio.Group
          style={{
            marginTop: 40,
          }}
          onChange={(e) => {
            const value = e.target.value;
            resetValuesFromVariant(generatedVariants[value]);
            setGeneratedVariantValue(value);
            setWarnWhen(true);
          }}
          options={generatedVariants.map((variant, index) => ({
            label: `${t('shared.behaviorExperiment.generatedVariant')} ${
              index + 1
            }`,
            value: index,
          }))}
          value={generatedVariantValue}
          optionType="button"
          buttonStyle="outline"
          defaultValue={0}
        />
      )}

      {hasGenerated && (
        <div
          style={{
            margin: 20,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 700,
          }}
        >
          <WarningOutlined
            style={{
              fontSize: 40,
              color: '#faad14',
              alignSelf: 'center',
            }}
          />

          {t('shared.behaviorExperiment.generatedWarning')}
        </div>
      )}
    </div>
  );

  return (
    <Create
      title={
        <div style={{ textWrap: 'wrap' }}>
          {t('shared.behaviorExperiment.title')}

          <Tooltip
            title={t('shared.behaviorExperiment.description')}
            overlayClassName={styles['behavior-experiment--tooltip']}
          >
            <QuestionCircleOutlined style={{ margin: '0 10px' }} />
          </Tooltip>
        </div>
      }
      isLoading={clientsLoading}
      saveButtonProps={{
        'onClick': () => saveData(),
        'loading': upsertBehaviorExperimentLoading || esLoading,
        'data-testid': 'cc-behavior-experiment-save',
        'disabled':
          !clientId ||
          isGenerating ||
          !(hypothesisA || hypothesisB) ||
          upsertBehaviorExperimentLoading ||
          esLoading ||
          behaviorExperimentLoading ||
          gptExperimentLoading ||
          unconvertedEsRow,
      }}
    >
      <div className={styles['behavior-experiment-container']}>
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>

      {unconvertedEsRow ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('pages.behaviorExperiment.unconvertedRow')}
          data-testid="cc-behavior-experiment-unconverted"
        />
      ) : null}

      {isEsRow ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('shared.behaviorExperiment.errors.SETUP_IMMUTABLE')}
          data-testid="cc-behavior-experiment-setup-locked"
          action={
            expreimentId ? (
              <Link
                to={`/tools/behaviorExperiments/show?behaviorExperimentId=${encodeURIComponent(
                  expreimentId,
                )}`}
              >
                <Button
                  size="small"
                  data-testid="cc-behavior-experiment-open-view"
                >
                  {t('pages.behaviorExperiment.openView')}
                </Button>
              </Link>
            ) : null
          }
        />
      ) : null}

      <Form<typeof initialValues>
        form={form}
        initialValues={{
          ...initialValues,
        }}
        onValuesChange={() => {
          setWarnWhen(true);
        }}
        onFinish={(values) => {}}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 16 }}
        labelWrap
        data-testid="cc-create-behavior-experiment-container"
        data-era={useEsPath ? 'es' : 'legacy'}
        data-es-row={isEsRow ? 'true' : 'false'}
      >
        <Form.Item
          name="purpose"
          label={t('shared.behaviorExperiment.purpose')}
          tooltip={t('shared.behaviorExperiment.purposesTip')}
          rules={[
            {
              required: true,
              message: t('shared.behaviorExperiment.purposeRequired'),
            },
          ]}
        >
          <Select
            placeholder={t('shared.behaviorExperiment.purpose')}
            data-testid="cc-create-behavior-experiment-purpose"
            disabled={isEsRow}
          >
            {memoizedPurposeOptions.map((option: any) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="design"
          label={t('shared.behaviorExperiment.design')}
          tooltip={t('shared.behaviorExperiment.designsTip')}
          rules={[
            {
              required: true,
              message: t('shared.behaviorExperiment.designRequired'),
            },
          ]}
        >
          <Radio.Group
            data-testid="cc-create-behavior-experiment-design"
            disabled={isEsRow}
          >
            {memoizedDesignsOptions.map((option: any) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="hypothesisType"
          label={t('shared.behaviorExperiment.hypothesisType')}
          tooltip={t('shared.behaviorExperiment.hypothesisTypeVariantsTip')}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select
            placeholder={t('shared.behaviorExperiment.hypothesisType')}
            data-testid="cc-create-behavior-experiment-hypothesis-type"
            disabled={isEsRow}
          >
            {memoizedHypotethisCountOptions.map((option: any) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="type" label={t('shared.behaviorExperiment.type')}>
          <Select
            placeholder={t('shared.behaviorExperiment.type')}
            data-testid="cc-create-behavior-experiment-type"
            disabled={isEsRow}
          >
            {memoizedTypeVariantsOptions.map((option: any) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {type === undefined ? null : type === 0 ? (
          <Form.Item
            name="activeSubtype"
            label={t('shared.behaviorExperiment.activeSubtype')}
          >
            <Select
              placeholder={t('shared.behaviorExperiment.activeSubtype')}
              data-testid="cc-create-behavior-experiment-active-subtype"
              disabled={isEsRow}
            >
              {memoizedActiveSubtypesOptions.map((option: any) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <Form.Item
            name="observationalSubtype"
            label={t('shared.behaviorExperiment.observationalSubtype')}
          >
            <Select
              placeholder={t('shared.behaviorExperiment.observationalSubtype')}
              data-testid="cc-create-behavior-experiment-observational-subtype"
              disabled={isEsRow}
            >
              {memoizedObservationalSubtypesOptions.map((option: any) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Divider />
        {(activeSubtype !== undefined ||
          observationalSubtype !== undefined) && (
          <Title level={4}>{t('shared.behaviorExperiment.hypotheses')}</Title>
        )}

        {(activeSubtype !== undefined || observationalSubtype !== undefined) &&
          (hypothesisType === 0 || hypothesisType === 1) && (
            <>
              <Form.Item
                name="hypothesisA"
                label={t('shared.behaviorExperiment.hypothesisA')}
                tooltip={t('shared.behaviorExperiment.hypothesisATip')}
              >
                <Input.TextArea
                  data-testid="cc-create-behavior-experiment-hypothesis-a"
                  placeholder={t(
                    'shared.behaviorExperiment.hypothesisAPlaceholder',
                  )}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </Form.Item>

              <div className={styles['behavior-experiments--slider-row']}>
                <Form.Item
                  label={t('shared.behaviorExperiment.hypothesisAStrength')}
                  name={'hypothesisAStrength'}
                >
                  <Slider
                    min={1}
                    max={100}
                    data-testid="cc-create-behavior-experiment-hypothesis-a-strength-slider"
                    style={{ minWidth: 200 }}
                  />
                </Form.Item>

                <Form.Item name={'hypothesisAStrength'}>
                  <InputNumber
                    min={1}
                    max={100}
                    data-testid="cc-create-behavior-experiment-hypothesis-a-strength-input"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="hypothesisAConfirmationCriteria"
                label={t(
                  'shared.behaviorExperiment.hypothesisAConfirmationCriteria',
                )}
              >
                <Input.TextArea
                  data-testid="cc-create-behavior-experiment-hypothesis-a-confirmation-criteria"
                  placeholder={t(
                    'shared.behaviorExperiment.hypothesisAConfirmationCriteria',
                  )}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </Form.Item>
            </>
          )}

        {(activeSubtype !== undefined || observationalSubtype !== undefined) &&
          (hypothesisType === 0 || hypothesisType === 1) &&
          generateArea}

        {(activeSubtype !== undefined || observationalSubtype !== undefined) &&
          (hypothesisType === 1 || hypothesisType === 2) && (
            <>
              <Form.Item
                name="hypothesisB"
                label={t('shared.behaviorExperiment.hypothesisB')}
                tooltip={t('shared.behaviorExperiment.hypothesisBTip')}
              >
                <Input.TextArea
                  data-testid="cc-create-behavior-experiment-hypothesis-b"
                  placeholder={t(
                    'shared.behaviorExperiment.hypothesisBPlaceholder',
                  )}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </Form.Item>

              <div className={styles['behavior-experiments--slider-row']}>
                <Form.Item
                  label={t('shared.behaviorExperiment.hypothesisBStrength')}
                  name={'hypothesisBStrength'}
                >
                  <Slider
                    min={1}
                    max={100}
                    data-testid="cc-create-behavior-experiment-hypothesis-b-strength-slider"
                    style={{ minWidth: 200 }}
                  />
                </Form.Item>

                <Form.Item name={'hypothesisBStrength'}>
                  <InputNumber
                    min={1}
                    max={100}
                    data-testid="cc-create-behavior-experiment-hypothesis-b-strength-input"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="hypothesisBConfirmationCriteria"
                label={t(
                  'shared.behaviorExperiment.hypothesisBConfirmationCriteria',
                )}
              >
                <Input.TextArea
                  data-testid="cc-create-behavior-experiment-hypothesis-b-confirmation-criteria"
                  placeholder={t(
                    'shared.behaviorExperiment.hypothesisBConfirmationCriteria',
                  )}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </Form.Item>
            </>
          )}

        {(activeSubtype !== undefined || observationalSubtype !== undefined) &&
          hypothesisType === 2 &&
          generateArea}

        {(activeSubtype !== undefined ||
          observationalSubtype !== undefined) && (
          <>
            <Divider />
            <Title level={4}>{t('shared.behaviorExperiment.settings')}</Title>

            <Form.Item
              name={['settings', 'experiment']}
              label={t('shared.behaviorExperiment.experiment')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-experiment"
                placeholder={t(
                  'shared.behaviorExperiment.expremientPlaceholder',
                )}
                autoSize={{ minRows: 4, maxRows: 8 }}
              />
            </Form.Item>

            <Form.Item
              name={['settings', 'time']}
              label={t('shared.behaviorExperiment.time')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-time"
                placeholder={t('shared.behaviorExperiment.time')}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>

            {(activeSubtype === 0 ||
              (observationalSubtype !== undefined &&
                observationalSubtype !== 2)) && (
              <Form.Item
                name={['settings', 'place']}
                label={t('shared.behaviorExperiment.place')}
              >
                <Input.TextArea
                  data-testid="cc-create-behavior-experiment-place"
                  placeholder={t('shared.behaviorExperiment.place')}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
            )}

            <Form.Item
              name={['settings', 'people']}
              label={t('shared.behaviorExperiment.people')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-people"
                placeholder={t('shared.behaviorExperiment.peoplePlaceholder')}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </Form.Item>

            <Form.Item
              name={['settings', 'resources']}
              label={t('shared.behaviorExperiment.resources')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-resources"
                placeholder={t(
                  'shared.behaviorExperiment.resourcesPlaceholder',
                )}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </Form.Item>

            {observationalSubtype === 1 && (
              <Form.Item
                name={['settings', 'survey']}
                label={t('shared.behaviorExperiment.survey')}
              >
                <Input.TextArea
                  data-testid="cc-create-behavior-experiment-survey"
                  placeholder={t('shared.behaviorExperiment.surveyPlaceholder')}
                  autoSize={{ minRows: 4, maxRows: 8 }}
                />
              </Form.Item>
            )}

            <Divider />
            <Title level={4}>
              {t('shared.behaviorExperiment.safetyAndCoping')}
            </Title>
            <Form.Item
              name="safetyBehaviors"
              label={t('shared.behaviorExperiment.safetyBehaviors')}
              tooltip={t('shared.behaviorExperiment.safetyBehaviorsTip')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-safety-behaviors"
                placeholder={t(
                  'shared.behaviorExperiment.safetyBehaviorsPlaceholder',
                )}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </Form.Item>

            <Form.Item
              name="helpfulCopingStrategies"
              label={t('shared.behaviorExperiment.helpfulCopingStrategies')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-helpful-coping-strategies"
                placeholder={t(
                  'shared.behaviorExperiment.helpfulCopingStrategiesPlaceholder',
                )}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </Form.Item>

            <Divider />

            <Title level={4}>{t('shared.behaviorExperiment.prediction')}</Title>

            <Form.Item
              name={['prediction', 'behaviors']}
              label={t('shared.behaviorExperiment.behaviors')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-behaviors"
                placeholder={t(
                  'shared.behaviorExperiment.behaviorsPlaceholder',
                )}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </Form.Item>

            <Form.Item
              name={['prediction', 'emotions', 'id']}
              label={t('shared.behaviorExperiment.expectedEmotions')}
            >
              <Select
                data-testid="cc-create-behavior-experiment-expected-emotions"
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={(emotionsIds) => {
                  const eventEmotions =
                    (emotionsIds as unknown as number[]).map((id) => {
                      const emotion = flattenedEmotions.find(
                        (e) => e.id === id,
                      );
                      if (emotion) {
                        const setEmotions = form.getFieldValue([
                          'prediction',
                          'emotions',
                        ]);
                        const existingEmotion = setEmotions.find(
                          (em: any) => em.id === id,
                        );
                        return {
                          id: emotion.id,
                          strength: !!existingEmotion
                            ? existingEmotion.strength
                            : 50,
                        };
                      }
                    }) || [];

                  form.setFieldValue(['prediction', 'emotions'], eventEmotions);
                }}
              >
                {baseEmotions.map((eg) => {
                  return (
                    <OptGroup label={eg.name} key={eg.name}>
                      {eg.children.map((e) => {
                        return (
                          <Option key={e.id} value={e.id}>
                            {e.name}
                          </Option>
                        );
                      })}
                    </OptGroup>
                  );
                })}
              </Select>
            </Form.Item>

            <div
              style={{
                display: 'none',
              }}
            >
              <Form.List name={['prediction', 'emotions']}>
                {(fields, { add, remove }, errors) => {
                  const predictionEmotions = form.getFieldValue([
                    'prediction',
                    'emotions',
                  ]);
                  return (
                    <>
                      {fields.map((field, index) => {
                        return (
                          <div
                            key={field.key}
                            className={
                              styles['behavior-experiments--slider-row']
                            }
                          >
                            <Form.Item
                              {...field}
                              label={
                                flattenedEmotions.find(
                                  (e) =>
                                    e.id === predictionEmotions[field.key].id,
                                )?.name || ''
                              }
                              key={'slider' + field.key}
                              name={[field.key, 'strength']}
                              labelCol={{ span: 10 }}
                              colon
                            >
                              <Slider
                                min={1}
                                max={100}
                                data-testid="cc-create-behavior-experiment-prediction-emotions-strength-slider"
                                style={{ minWidth: 200 }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={'input' + field.key}
                              name={[field.key, 'strength']}
                            >
                              <InputNumber
                                min={1}
                                max={100}
                                data-testid="cc-create-behavior-experiment-prediction-emotions-strength-input"
                              />
                            </Form.Item>
                          </div>
                        );
                      })}
                    </>
                  );
                }}
              </Form.List>
            </div>

            <Form.Item
              label={t('shared.behaviorExperiment.expectedFeelings')}
              name={['prediction', 'feelings']}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="children"
                data-testid="cc-create-behavior-experiment-expected-feelings"
              >
                {baseFeelings.map((bg) => {
                  return (
                    <OptGroup label={bg.name} key={bg.name}>
                      {bg.children.map((e) => {
                        return (
                          <Option key={e.id} value={e.id}>
                            {e.name}
                          </Option>
                        );
                      })}
                    </OptGroup>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              name={['prediction', 'otherPredictions']}
              label={t('shared.behaviorExperiment.otherPredictions')}
            >
              <Input.TextArea
                data-testid="cc-create-behavior-experiment-other-predictions"
                placeholder={t(
                  'shared.behaviorExperiment.otherPredictionsPlaceholder',
                )}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </Form.Item>

            {useEsPath ? (
              // E02: for an ES client the outcome lives in the experiment view — observations
              // (many, editable) and the completion (portal-per-step resolved_v2 (d)+(e))
              <>
                <Divider />
                <Alert
                  type="info"
                  showIcon
                  data-testid="cc-behavior-experiment-outcome-hint"
                  message={t(
                    isEsRow && expreimentId
                      ? 'pages.behaviorExperiment.openViewHint'
                      : 'pages.behaviorExperiment.saveToStart',
                  )}
                  action={
                    isEsRow && expreimentId ? (
                      <Link
                        to={`/tools/behaviorExperiments/show?behaviorExperimentId=${encodeURIComponent(
                          expreimentId,
                        )}`}
                      >
                        <Button
                          size="small"
                          type="primary"
                          data-testid="cc-behavior-experiment-open-view-outcome"
                        >
                          {t('pages.behaviorExperiment.openView')}
                        </Button>
                      </Link>
                    ) : null
                  }
                />
              </>
            ) : (
              <>
                <Divider />
                <Title level={4}>
                  {t('shared.behaviorExperiment.outcome')}
                </Title>
                <Form.Item
                  name={['outcome', 'behaviors']}
                  label={t('shared.behaviorExperiment.actualBehavior')}
                >
                  <Input.TextArea
                    data-testid="cc-create-behavior-experiment-actual-behavior"
                    placeholder={t(
                      'shared.behaviorExperiment.actualBehaviorPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>

                <Form.Item
                  name={['outcome', 'emotions', 'id']}
                  label={t('shared.behaviorExperiment.actualEmotions')}
                >
                  <Select
                    data-testid="cc-create-behavior-experiment-actual-emotions"
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={(emotionsIds) => {
                      const eventEmotions =
                        (emotionsIds as unknown as number[]).map((id) => {
                          const emotion = flattenedEmotions.find(
                            (e) => e.id === id,
                          );
                          if (emotion) {
                            const outcomeEmotions = form.getFieldValue([
                              'outcome',
                              'emotions',
                            ]);

                            const existingEmotion = outcomeEmotions.find(
                              (em: any) => em.id === id,
                            );
                            return {
                              id: emotion.id,
                              strength: !!existingEmotion
                                ? existingEmotion.strength
                                : 50,
                            };
                          }
                        }) || [];

                      form.setFieldValue(
                        ['outcome', 'emotions'],
                        eventEmotions,
                      );
                    }}
                  >
                    {baseEmotions.map((eg) => {
                      return (
                        <OptGroup label={eg.name} key={eg.name}>
                          {eg.children.map((e) => {
                            return (
                              <Option key={e.id} value={e.id}>
                                {e.name}
                              </Option>
                            );
                          })}
                        </OptGroup>
                      );
                    })}
                  </Select>
                </Form.Item>

                {!!outcome?.emotions?.length && (
                  <Title level={5} style={{ textAlign }}>
                    {t('createThought.rateEmotions')}:
                  </Title>
                )}

                <Form.List name={['outcome', 'emotions']}>
                  {(fields, { add, remove }, errors) => {
                    const outcomeEmotions = form.getFieldValue([
                      'outcome',
                      'emotions',
                    ]);
                    return (
                      <>
                        {fields.map((field, index) => {
                          return (
                            <div
                              key={field.key}
                              className={
                                styles['behavior-experiments--slider-row']
                              }
                            >
                              <Form.Item
                                {...field}
                                label={
                                  flattenedEmotions.find(
                                    (e) =>
                                      e.id === outcomeEmotions[field.key].id,
                                  )?.name || ''
                                }
                                key={'slider' + field.key}
                                name={[field.key, 'strength']}
                                labelCol={{ span: 10 }}
                                colon
                              >
                                <Slider
                                  min={1}
                                  max={100}
                                  data-testid="cc-create-behavior-experiment-actual-emotions-strength-slider"
                                  style={{ minWidth: 200 }}
                                />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                key={'input' + field.key}
                                name={[field.key, 'strength']}
                              >
                                <InputNumber
                                  min={1}
                                  max={100}
                                  data-testid="cc-create-behavior-experiment-actual-emotions-strength-slider"
                                />
                              </Form.Item>
                            </div>
                          );
                        })}
                      </>
                    );
                  }}
                </Form.List>

                <Form.Item
                  label={t('shared.behaviorExperiment.actualFeelings')}
                  name={['outcome', 'feelings']}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    data-testid="cc-create-behavior-experiment-actual-feelings"
                  >
                    {baseFeelings.map((bg) => {
                      return (
                        <OptGroup label={bg.name} key={bg.name}>
                          {bg.children.map((e) => {
                            return (
                              <Option key={e.id} value={e.id}>
                                {e.name}
                              </Option>
                            );
                          })}
                        </OptGroup>
                      );
                    })}
                  </Select>
                </Form.Item>

                <Form.Item
                  name={['outcome', 'patientReflections']}
                  label={t('shared.behaviorExperiment.patientReflections')}
                >
                  <Input.TextArea
                    data-testid="cc-create-behavior-experiment-patient-reflections"
                    placeholder={t(
                      'shared.behaviorExperiment.patientReflectionsPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>

                <Form.Item
                  name={['outcome', 'therapistReflections']}
                  label={t('shared.behaviorExperiment.therapistReflections')}
                >
                  <Input.TextArea
                    data-testid="cc-create-behavior-experiment-therapist-reflections"
                    placeholder={t(
                      'shared.behaviorExperiment.therapistReflectionsPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>

                <Form.Item
                  name={['outcome', 'newLearnings']}
                  label={t('shared.behaviorExperiment.newLearnings')}
                >
                  <Input.TextArea
                    data-testid="cc-create-behavior-experiment-new-learnings"
                    placeholder={t(
                      'shared.behaviorExperiment.newLearningsPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>

                <Form.Item
                  name={['outcome', 'meaningForOriginalBelief']}
                  label={t(
                    'shared.behaviorExperiment.meaningForOriginalBelief',
                  )}
                >
                  <Input.TextArea
                    data-testid="cc-create-behavior-experiment-meaning-for-original-belief"
                    placeholder={t(
                      'shared.behaviorExperiment.meaningForOriginalBeliefPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>

                <div className={styles['behavior-experiments--slider-row']}>
                  <Form.Item
                    label={t('shared.behaviorExperiment.oldThoughtStrength')}
                    name={['outcome', 'oldThoughtStrength']}
                  >
                    <Slider
                      min={1}
                      max={100}
                      data-testid="cc-create-behavior-experiment-old-thought-strength-slider"
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>

                  <Form.Item name={['outcome', 'oldThoughtStrength']}>
                    <InputNumber
                      min={1}
                      max={100}
                      data-testid="cc-create-behavior-experiment-old-thought-strength-input"
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name={['outcome', 'newThought']}
                  label={t('shared.behaviorExperiment.newThought')}
                >
                  <Input.TextArea
                    data-testid="cc-create-behavior-experiment-new-thought"
                    placeholder={t(
                      'shared.behaviorExperiment.newThoughtPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>

                <div className={styles['behavior-experiments--slider-row']}>
                  <Form.Item
                    name={['outcome', 'newThoughtStrength']}
                    label={t('shared.behaviorExperiment.newThoughtStrength')}
                  >
                    <Slider
                      min={1}
                      max={100}
                      data-testid="cc-create-behavior-experiment-new-thought-strength-slider"
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>

                  <Form.Item name={['outcome', 'newThoughtStrength']}>
                    <InputNumber
                      min={1}
                      max={100}
                      data-testid="cc-create-behavior-experiment-new-thought-strength-input"
                    />
                  </Form.Item>
                </div>

                <Divider />
                <Title level={4}>
                  {t('shared.behaviorExperiment.followUp')}
                </Title>
                <Form.Item
                  name={'followUpExperimentsIdeas'}
                  label={t(
                    'shared.behaviorExperiment.followUpExperimentsIdeas',
                  )}
                >
                  <Input.TextArea
                    placeholder={t(
                      'shared.behaviorExperiment.followUpExperimentsIdeasPlaceholder',
                    )}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    data-testid="cc-create-behavior-experiment-follow-up-experiments-ideas"
                  />
                </Form.Item>
              </>
            )}
          </>
        )}
      </Form>
    </Create>
  );
};
